import {useEffect, useRef, useState} from 'react';

const TICK_MS = 50;
/** Time (ms) over which the bar approaches 99% while waiting. Never shows 100% until success. */
const APPROACH_99_PERCENT_MS = 20000;
const MAX_PERCENT_WHILE_LOADING = 99;

export interface MatchProgressBarProps {
  /** True while the request is in progress; bar fills toward 99% and stops there. */
  animating: boolean;
  /** True when response is back; bar shows 100% green before parent hides after 0.5s. Defaults to false. */
  success?: boolean;
}

export function MatchProgressBar({animating, success = false}: MatchProgressBarProps) {
  const [totalElapsedMs, setTotalElapsedMs] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);

  useEffect(() => {
    if (!animating) return;
    elapsedRef.current = 0;
    setTotalElapsedMs(0);
  }, [animating]);

  useEffect(() => {
    if (!animating) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      elapsedRef.current += TICK_MS;
      setTotalElapsedMs(elapsedRef.current);
    }, TICK_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [animating]);

  const fillPercent = success
    ? 100
    : Math.min(
      MAX_PERCENT_WHILE_LOADING,
      (totalElapsedMs / APPROACH_99_PERCENT_MS) * MAX_PERCENT_WHILE_LOADING
    );
  const fillClassName = success
    ? 'company-matching__progress-bar-fill company-matching__progress-bar-fill--success'
    : 'company-matching__progress-bar-fill company-matching__progress-bar-fill--animating';

  return (
    <div className='company-matching__progress-bar' aria-hidden>
      <div
        className={fillClassName}
        style={{width: `${fillPercent}%`}}
      />
    </div>
  );
}
